import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner, ProgressBar } from 'react-bootstrap';
import { Search, XCircle, Download } from 'lucide-react';
import "bootstrap/dist/css/bootstrap.min.css";
import { getWhatsappOffers, getWhatsappOffersList, exportWhatsappData } from '../../actions/whatsappAnalytics';
import { GET_WA_OFFERS, GET_WA_OFFERS_LIST } from '../../actions/types';
import { setAlert } from '../../actions/alert';
import DateRangeBar, { useDateRange } from './DateRangeBar';
import Pager from './Pager';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';
import Metric from './Metric';
import { downloadXlsx } from './exportXlsx';
import {
    OFFER_LABELS,
    OFFER_STATUS_LABELS,
    NON_CYCLE_OFFERS,
    isCycleKey,
    offerLabel,
    formatCycle,
    formatMoney,
    formatAge,
    formatHours,
    formatIstDateTime
} from './labels';

const PAGE_SIZE = 20;
const NO_VALUE = '-';
const TIMELINE_PATH = '/inventory/v1.2/whatsapp/timeline';
const ORDER_PATH = '/inventory/v1.2/orders';
const OFFER_KEYS = Object.keys(OFFER_LABELS);
const STATUSES = Object.keys(OFFER_STATUS_LABELS);
const FILTER_KEYS = ['offerKey', 'status', 'search'];
const INITIAL_FILTERS = { offerKey: 'all', status: 'all', search: '' };
const ALL_CYCLES = 'all';

const STATUS_VARIANTS = {
    offered: 'secondary',
    link_created: 'info',
    paid: 'success',
    failed: 'danger',
    expired: 'warning',
    closed: 'dark'
};

// Stack order in the status mix bar: good outcomes first
const MIX_ORDER = ['paid', 'link_created', 'offered', 'failed', 'expired', 'closed'];

const formatCount = (value) => (Number.isFinite(value) ? value.toLocaleString('en-IN') : NO_VALUE);

const percentOf = (part, whole) => (whole > 0 ? Math.round((part / whole) * 1000) / 10 : null);

const formatPercent = (value) => (value === null ? NO_VALUE : `${value}%`);

const filtersFromQuery = (params) => {
    const filters = { ...INITIAL_FILTERS };
    FILTER_KEYS.forEach((key) => {
        if (params.get(key)) filters[key] = params.get(key);
    });
    return filters;
};

// One card per offer. Welcome offers have no cycle so they are always shown; pack rows follow the cycle selector.
export const buildCards = (offers, cycle) => OFFER_KEYS.map((offerKey) => {
    const rows = offers.filter((row) => (
        row.offerKey === offerKey && (!isCycleKey(row.cycleKey) || cycle === ALL_CYCLES || row.cycleKey === cycle)
    ));
    const card = {
        offerKey,
        cycleBased: !NON_CYCLE_OFFERS.includes(offerKey),
        offered: 0,
        linkCreated: 0,
        paid: 0,
        revenue: 0,
        byStatus: {}
    };
    let payMinutes = 0;
    rows.forEach((row) => {
        card.offered += row.offered;
        card.linkCreated += row.linkCreated;
        card.paid += row.paid;
        card.revenue += row.revenue;
        Object.entries(row.byStatus).forEach(([status, count]) => {
            card.byStatus[status] = (card.byStatus[status] || 0) + count;
        });
        payMinutes += (row.avgMinutesToPay || 0) * row.paid;
    });
    card.conversionPercent = percentOf(card.paid, card.offered);
    if (rows.length === 1) card.avgMinutesToPay = rows[0].avgMinutesToPay;
    else card.avgMinutesToPay = card.paid > 0 ? Math.round((payMinutes / card.paid) * 100) / 100 : null;
    return card;
});

export const buildTotals = (cards) => {
    const offered = cards.reduce((sum, card) => sum + card.offered, 0);
    const paid = cards.reduce((sum, card) => sum + card.paid, 0);
    return {
        offered,
        paid,
        revenue: cards.reduce((sum, card) => sum + card.revenue, 0),
        conversionPercent: percentOf(paid, offered)
    };
};

const exportRow = (row) => ({
    'Offered at (IST)': formatIstDateTime(row.offeredAt),
    Offer: offerLabel(row.offerKey),
    Cycle: isCycleKey(row.cycleKey) ? formatCycle(row.cycleKey) : '',
    Status: OFFER_STATUS_LABELS[row.status] || row.status,
    Price: row.price ?? '',
    Cans: row.cans ?? '',
    'Paid at (IST)': row.paidAt ? formatIstDateTime(row.paidAt) : '',
    Customer: row.userName || '',
    Phone: row.phone || '',
    'User id': row.userId || '',
    'Order id': row.order || ''
});

const StatusMix = ({ byStatus, offered }) => {
    if (!offered) return <div className="text-muted small">No offers in this view.</div>;
    const present = MIX_ORDER.filter((status) => byStatus[status] > 0);
    return (
        <>
            <ProgressBar style={{ height: '0.6rem' }} aria-label="Offer status mix">
                {present.map((status) => (
                    <ProgressBar
                        key={status}
                        variant={STATUS_VARIANTS[status]}
                        now={(byStatus[status] / offered) * 100}
                        title={`${OFFER_STATUS_LABELS[status]}: ${byStatus[status]}`}
                    />
                ))}
            </ProgressBar>
            <div className="d-flex flex-wrap gap-2 mt-2 small text-muted">
                {present.map((status) => (
                    <span key={status}>
                        <Badge bg={STATUS_VARIANTS[status]} className="me-1">{byStatus[status]}</Badge>
                        {OFFER_STATUS_LABELS[status]}
                    </span>
                ))}
            </div>
        </>
    );
};

StatusMix.propTypes = {
    byStatus: PropTypes.object.isRequired,
    offered: PropTypes.number.isRequired
};

const OfferCard = ({ card, selected, onSelect }) => (
    <Card className={`shadow-sm h-100 ${selected ? 'border-primary' : ''}`}>
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
            <span className="fw-semibold">{offerLabel(card.offerKey)}</span>
            {!card.cycleBased && <span className="text-muted small">Not cycle based</span>}
        </Card.Header>
        <Card.Body>
            <Row className="g-3 mb-3">
                <Col xs={6}><Metric label="Offered" value={formatCount(card.offered)} /></Col>
                <Col xs={6}>
                    <Metric
                        label="Link created"
                        value={formatCount(card.linkCreated)}
                        hint={`${formatPercent(percentOf(card.linkCreated, card.offered))} of offered`}
                    />
                </Col>
                <Col xs={6}>
                    <Metric
                        label="Paid"
                        value={formatCount(card.paid)}
                        hint={`${formatPercent(card.conversionPercent)} conversion`}
                    />
                </Col>
                <Col xs={6}><Metric label="Revenue" value={formatMoney(card.revenue)} /></Col>
                <Col xs={12}>
                    <Metric
                        label="Average time to pay"
                        value={card.avgMinutesToPay === null ? NO_VALUE : formatHours(card.avgMinutesToPay / 60)}
                    />
                </Col>
            </Row>
            <StatusMix byStatus={card.byStatus} offered={card.offered} />
        </Card.Body>
        <Card.Footer className="bg-white">
            <Button size="sm" variant={selected ? 'primary' : 'outline-primary'} onClick={() => onSelect(card.offerKey)}>
                {selected ? 'Showing these offers' : 'Show these offers'}
            </Button>
        </Card.Footer>
    </Card>
);

OfferCard.propTypes = {
    card: PropTypes.object.isRequired,
    selected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired
};

const Offers = ({
    offers, offersList, pending, error,
    getWhatsappOffers, getWhatsappOffersList, exportWhatsappData, setAlert
}) => {
    const [searchParams] = useSearchParams();
    const [initialQuery] = useState(() => ({
        filters: filtersFromQuery(searchParams),
        range: { from: searchParams.get('from'), to: searchParams.get('to') }
    }));
    const { range, setRange } = useDateRange(initialQuery.range);
    const [cycle, setCycle] = useState(ALL_CYCLES);
    const [formData, setFormData] = useState(initialQuery.filters);
    const [appliedFilters, setAppliedFilters] = useState(initialQuery.filters);
    const [page, setPage] = useState(1);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        document.title = 'WhatsApp Offers';
    }, []);

    const loadCards = useCallback(() => {
        getWhatsappOffers({ from: range.from, to: range.to });
    }, [getWhatsappOffers, range.from, range.to]);

    const loadList = useCallback(() => {
        getWhatsappOffersList({
            page,
            limit: PAGE_SIZE,
            from: range.from,
            to: range.to,
            cycleKey: cycle,
            ...appliedFilters
        });
    }, [getWhatsappOffersList, page, range.from, range.to, cycle, appliedFilters]);

    useEffect(() => {
        loadCards();
    }, [loadCards]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    const cardsReady = Boolean(offers && offers.range.from === range.from && offers.range.to === range.to);

    const cycleOptions = useMemo(() => {
        const keys = new Set();
        if (cardsReady) offers.offers.forEach((row) => isCycleKey(row.cycleKey) && keys.add(row.cycleKey));
        if (cycle !== ALL_CYCLES) keys.add(cycle);
        return Array.from(keys).sort().reverse();
    }, [cardsReady, offers, cycle]);

    const cards = useMemo(() => (cardsReady ? buildCards(offers.offers, cycle) : []), [cardsReady, offers, cycle]);
    const totals = useMemo(() => buildTotals(cards), [cards]);

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setAppliedFilters({ ...formData, search: formData.search.trim() });
        setPage(1);
    };

    const clearFilters = () => {
        setFormData(INITIAL_FILTERS);
        setAppliedFilters(INITIAL_FILTERS);
        setCycle(ALL_CYCLES);
        setPage(1);
    };

    const selectOffer = (offerKey) => {
        const next = { ...appliedFilters, offerKey: appliedFilters.offerKey === offerKey ? 'all' : offerKey };
        setFormData({ ...formData, offerKey: next.offerKey });
        setAppliedFilters(next);
        setPage(1);
    };

    const onCycleChange = (e) => {
        setCycle(e.target.value);
        setPage(1);
    };

    const onRangeChange = (next) => {
        setRange(next);
        setCycle(ALL_CYCLES);
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const rows = await exportWhatsappData('offers', range);
            if (rows === null) return;
            if (rows.length === 0) {
                setAlert('No offers in this range to export', 'info');
                return;
            }
            downloadXlsx(rows.map(exportRow), `whatsapp_offers_${range.from}_to_${range.to}`);
        } finally {
            setExporting(false);
        }
    };

    const renderCards = () => {
        if (!cardsReady) {
            return error && !pending[GET_WA_OFFERS] ? <ErrorBanner message={error} onRetry={loadCards} /> : <LoadingState />;
        }
        return (
            <>
                {totals.offered === 0 && <EmptyState>No offers were made in this range.</EmptyState>}
                <Card className="shadow-sm mb-4">
                    <Card.Body>
                        <Row className="g-3">
                            <Col xs={6} md={3}><Metric label="Offers made" value={formatCount(totals.offered)} /></Col>
                            <Col xs={6} md={3}><Metric label="Paid" value={formatCount(totals.paid)} hint={`${formatPercent(totals.conversionPercent)} conversion`} /></Col>
                            <Col xs={6} md={3}><Metric label="Revenue" value={formatMoney(totals.revenue)} /></Col>
                            <Col xs={6} md={3} className="text-muted small align-self-center">
                                Figures cover offers made in the range, including payments made after it.
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
                <Row className="g-3 mb-4">
                    {cards.map((card) => (
                        <Col key={card.offerKey} xs={12} lg={4}>
                            <OfferCard card={card} selected={appliedFilters.offerKey === card.offerKey} onSelect={selectOffer} />
                        </Col>
                    ))}
                </Row>
            </>
        );
    };

    const renderTable = () => {
        if (pending[GET_WA_OFFERS_LIST]) {
            return <LoadingState />;
        }
        if (offersList.rows.length === 0) {
            return <EmptyState className="m-3">No offers match these filters.</EmptyState>;
        }
        return (
            <Table responsive hover className="mb-0 align-middle">
                <thead className="table-light">
                    <tr>
                        <th>Offered (IST)</th>
                        <th>Customer</th>
                        <th>Offer</th>
                        <th>Cycle</th>
                        <th>Status</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">Cans</th>
                        <th>Paid (IST)</th>
                        <th>Time to pay</th>
                        <th>Order</th>
                    </tr>
                </thead>
                <tbody>
                    {offersList.rows.map((row) => (
                        <tr key={row.id}>
                            <td className="text-nowrap">{formatIstDateTime(row.offeredAt)}</td>
                            <td>
                                {row.user ? (
                                    <>
                                        <Link to={`${TIMELINE_PATH}/${row.user.id}`} className="fw-semibold">
                                            {row.user.name || 'Unnamed'}
                                        </Link>
                                        <div className="text-muted small">{row.user.phone}</div>
                                    </>
                                ) : (
                                    <span className="text-muted">Deleted user</span>
                                )}
                            </td>
                            <td>{offerLabel(row.offerKey)}</td>
                            <td>{isCycleKey(row.cycleKey) ? formatCycle(row.cycleKey) : NO_VALUE}</td>
                            <td><Badge bg={STATUS_VARIANTS[row.status] || 'secondary'}>{OFFER_STATUS_LABELS[row.status] || row.status}</Badge></td>
                            <td className="text-end">{row.price === null ? NO_VALUE : formatMoney(row.price)}</td>
                            <td className="text-end">{row.cans === null ? NO_VALUE : row.cans}</td>
                            <td className="text-nowrap">{row.paidAt ? formatIstDateTime(row.paidAt) : NO_VALUE}</td>
                            <td className="text-nowrap">{row.paidAt ? formatAge(row.offeredAt, row.paidAt) : NO_VALUE}</td>
                            <td>
                                {row.order ? <Link to={`${ORDER_PATH}/${row.order}`}>View order</Link> : NO_VALUE}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Offers</h2>
                    <p className="text-muted mb-0">
                        Offers made in the range and what came of them. Days are IST. Pack offers start a new cycle each month.
                    </p>
                </Col>
                <Col xs={12} lg="auto">
                    <DateRangeBar range={range} onChange={onRangeChange} />
                </Col>
            </Row>

            {renderCards()}

            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Offer</Form.Label>
                                    <Form.Select name="offerKey" value={formData.offerKey} onChange={onChange}>
                                        <option value="all">All offers</option>
                                        {OFFER_KEYS.map((key) => (
                                            <option key={key} value={key}>{offerLabel(key)}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Pack cycle (month)</Form.Label>
                                    <Form.Select value={cycle} onChange={onCycleChange} aria-label="Pack cycle">
                                        <option value={ALL_CYCLES}>All cycles</option>
                                        {cycleOptions.map((key) => (
                                            <option key={key} value={key}>{formatCycle(key)}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Text muted>Welcome offers have no cycle and are hidden in the table when one is picked.</Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Status</Form.Label>
                                    <Form.Select name="status" value={formData.status} onChange={onChange}>
                                        <option value="all">All statuses</option>
                                        {STATUSES.map((status) => (
                                            <option key={status} value={status}>{OFFER_STATUS_LABELS[status]}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={3}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">Customer</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="search"
                                        value={formData.search}
                                        onChange={onChange}
                                        placeholder="Name, phone or user id"
                                        maxLength={60}
                                    />
                                </Form.Group>
                            </Col>
                            <Col xs={12} className="d-flex flex-wrap gap-2">
                                <Button type="submit" variant="primary">
                                    <Search className="me-2" size={16} />
                                    Apply Filters
                                </Button>
                                <Button type="button" variant="outline-secondary" onClick={clearFilters}>
                                    <XCircle className="me-2" size={16} />
                                    Clear
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline-primary"
                                    className="ms-lg-auto"
                                    onClick={handleExport}
                                    disabled={exporting}
                                    title="Exports every offer made in the selected date range; the filters above are not applied"
                                >
                                    {exporting ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <Download size={16} className="me-2" />
                                            Export range (xlsx)
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {error && cardsReady && <ErrorBanner message={error} onRetry={loadList} />}

            <Card className="shadow-sm">
                <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Offers</span>
                    <span className="text-muted small">{offersList.total.toLocaleString('en-IN')} in total</span>
                </Card.Header>
                <Card.Body className="p-0">
                    {renderTable()}
                    <Pager page={page} pages={offersList.pages} onChange={setPage} />
                </Card.Body>
            </Card>
        </Container>
    );
};

Offers.propTypes = {
    offers: PropTypes.object,
    offersList: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        total: PropTypes.number.isRequired,
        pages: PropTypes.number.isRequired
    }).isRequired,
    pending: PropTypes.object.isRequired,
    error: PropTypes.string,
    getWhatsappOffers: PropTypes.func.isRequired,
    getWhatsappOffersList: PropTypes.func.isRequired,
    exportWhatsappData: PropTypes.func.isRequired,
    setAlert: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    offers: state.whatsappAnalytics.offers,
    offersList: state.whatsappAnalytics.offersList,
    pending: state.whatsappAnalytics.pending,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, {
    getWhatsappOffers,
    getWhatsappOffersList,
    exportWhatsappData,
    setAlert
})(Offers);
